// frontend/src/components/employee/AddPropertyModal.js

import { useState, useEffect } from 'react';
import api from '../../utils/api';

const lbl = { fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 };
const inp = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', outline: 'none', background: 'white', boxSizing: 'border-box' };

const EMPTY = { name: '', address: '', city: '', state: '', zip: '', landlord_id: '', unit_count: '', isp: '', base_monthly_cost: '', our_margin: '', status: 'draft' };
const ISP_LIST = ['Spectrum', 'Optimum', 'RCN', 'Verizon Fios', 'Comcast', 'Astound', 'Cox', 'Other'];

export default function AddPropertyModal({ onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [landlords, setLandlords] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  useEffect(() => {
    api.get('/employee/landlords').then(res => setLandlords(res.data.landlords || [])).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.address || !form.landlord_id) { setError('Address and landlord are required.'); return; }
    setSaving(true); setError('');
    try {
      const res = await api.post('/employee/properties', { ...form, unit_count: parseInt(form.unit_count) || 0, base_monthly_cost: parseFloat(form.base_monthly_cost) || null, our_margin: parseFloat(form.our_margin) || null });
      setCreated(res.data.property);
      if (onSuccess) onSuccess(res.data.property);
    } catch (err) { setError(err.response?.data?.error || 'Could not create property'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Add New Property</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px 24px' }}>
          {!created ? (
            <>
              {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={lbl}>Landlord *</label>
                  <select value={form.landlord_id} onChange={e => setForm(p => ({ ...p, landlord_id: e.target.value }))} style={inp}>
                    <option value="">Select landlord...</option>
                    {landlords.map(l => <option key={l.id} value={l.id}>{l.first_name} {l.last_name}{l.company_name ? ' — ' + l.company_name : ''}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={lbl}>Property Name</label>
                  <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={inp} placeholder="e.g. 45 Thayer Street" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={lbl}>Street Address *</label>
                  <input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} style={inp} placeholder="45 Thayer Street" />
                </div>
                <div>
                  <label style={lbl}>City</label>
                  <input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} style={inp} placeholder="New York" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={lbl}>State</label>
                    <input value={form.state} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} style={inp} placeholder="NY" maxLength={2} />
                  </div>
                  <div>
                    <label style={lbl}>ZIP</label>
                    <input value={form.zip} onChange={e => setForm(p => ({ ...p, zip: e.target.value }))} style={inp} placeholder="10040" />
                  </div>
                </div>
                <div>
                  <label style={lbl}>Total Units</label>
                  <input type="number" value={form.unit_count} onChange={e => setForm(p => ({ ...p, unit_count: e.target.value }))} style={inp} placeholder="54" />
                </div>
                <div>
                  <label style={lbl}>ISP Provider</label>
                  <select value={form.isp} onChange={e => setForm(p => ({ ...p, isp: e.target.value }))} style={inp}>
                    <option value="">Select ISP...</option>
                    {ISP_LIST.map(i => <option key={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>ISP Base Cost / mo ($)</label>
                  <input type="number" step="0.01" value={form.base_monthly_cost} onChange={e => setForm(p => ({ ...p, base_monthly_cost: e.target.value }))} style={inp} placeholder="45.00" />
                </div>
                <div>
                  <label style={lbl}>Our Margin / unit ($)</label>
                  <input type="number" step="0.01" value={form.our_margin} onChange={e => setForm(p => ({ ...p, our_margin: e.target.value }))} style={inp} placeholder="7.00" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={lbl}>Initial Status</label>
                  <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} style={inp}>
                    <option value="draft">Draft — not visible to landlord yet</option>
                    <option value="active">Active — visible immediately</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
                <button onClick={handleSave} disabled={saving} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Creating...' : 'Create Property'}
                </button>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <h3 style={{ margin: '0 0 8px', fontSize: 20 }}>Property Created!</h3>
              <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>{created.name} has been added with status <strong>{created.status}</strong>.</p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button onClick={() => { setCreated(null); setForm(EMPTY); }} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, cursor: 'pointer' }}>Add Another</button>
                <button onClick={onClose} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
